import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import { IconBan, IconArrowBackUp, IconRefresh } from '@tabler/icons-react';
import type { LicenseKey, LicenseKeyStatus } from '@techgitter/contracts';
import {
  fetchLicenseKeys,
  revokeLicenseKey,
  voidLicenseKey,
  unvoidLicenseKey,
} from '../services/licenseKeysApi';
import {
  DataTableCard,
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
} from '../components/table';
import { Chip } from '../components/chip';
import { LicenseProductsCard } from '../components/licenses/LicenseProductsCard';
import {
  SecondaryToolbar,
  ToolbarSearch,
  ToolbarSelect,
  ToolbarSpacer,
  ToolbarCount,
} from '../components/toolbar';

type StatusFilter = 'all' | LicenseKeyStatus;

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'available', label: 'Available' },
  { value: 'allocated', label: 'Allocated' },
  { value: 'void', label: 'Void' },
];

const STATUS_TONE: Record<LicenseKeyStatus, 'success' | 'info' | 'neutral'> = {
  available: 'success',
  allocated: 'info',
  void: 'neutral',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return iso.slice(0, 10);
}

function formatCost(cost: number | null): string {
  return cost == null ? '—' : `$${cost.toFixed(2)}`;
}

/** Admin "License Inventory" — view pre-purchased resale keys, see who holds
 *  each, and revoke / void / un-void them. Stock is added via the Part Orders
 *  receive flow, not here. */
export const LicenseInventoryAdminPage: React.FC = () => {
  const [keys, setKeys] = useState<LicenseKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(
    null,
  );

  // Reason dialog drives both revoke and void (each takes an optional reason).
  const [action, setAction] = useState<{ key: LicenseKey; kind: 'revoke' | 'void' } | null>(null);
  const [reason, setReason] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = useCallback(
    async (signal?: AbortSignal) => {
      try {
        setLoading(true);
        setLoadError(null);
        const data = await fetchLicenseKeys(
          { status: status === 'all' ? undefined : status },
          signal,
        );
        setKeys(data);
      } catch (e) {
        if ((e as DOMException)?.name === 'AbortError') return;
        setLoadError(e instanceof Error ? e.message : 'Failed to load license keys');
      } finally {
        setLoading(false);
      }
    },
    [status],
  );

  useEffect(() => {
    const controller = new AbortController();
    refresh(controller.signal);
    return () => controller.abort();
  }, [refresh]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return keys;
    return keys.filter((k) =>
      [k.key, k.sku, k.productName, k.currentCustomerName, k.currentJobNumber]
        .filter(Boolean)
        .some((v) => (v as string).toLowerCase().includes(q)),
    );
  }, [keys, search]);

  const closeAction = () => {
    setAction(null);
    setReason('');
  };

  const runAction = async () => {
    if (!action) return;
    const { key, kind } = action;
    setBusyId(key.id);
    try {
      if (kind === 'revoke') {
        await revokeLicenseKey(key.id, { reason: reason.trim() || undefined });
        setSnackbar({ message: 'Key revoked — returned to the pool', severity: 'success' });
      } else {
        await voidLicenseKey(key.id, { reason: reason.trim() || undefined });
        setSnackbar({ message: 'Key voided', severity: 'success' });
      }
      closeAction();
      await refresh();
    } catch (e) {
      setSnackbar({
        message: e instanceof Error ? e.message : `Failed to ${kind} key`,
        severity: 'error',
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleUnvoid = async (key: LicenseKey) => {
    setBusyId(key.id);
    try {
      await unvoidLicenseKey(key.id);
      setSnackbar({ message: 'Key returned to the pool', severity: 'success' });
      await refresh();
    } catch (e) {
      setSnackbar({
        message: e instanceof Error ? e.message : 'Failed to un-void key',
        severity: 'error',
      });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box sx={{ background: '#f0f4f9', minHeight: '100%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <Box sx={{ px: '16px', pt: '10px' }}>
        <LicenseProductsCard />
      </Box>

      <SecondaryToolbar floating>
        <ToolbarSelect
          label="Status"
          value={status}
          onChange={(v) => setStatus(v as StatusFilter)}
          options={STATUS_OPTIONS}
        />
        <ToolbarSearch
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search key, SKU, customer, ticket…"
        />
        <ToolbarSpacer />
        <ToolbarCount>{visible.length} key(s)</ToolbarCount>
        <Tooltip title="Refresh">
          <span>
            <IconButton size="small" onClick={() => refresh()} disabled={loading}>
              <IconRefresh size={18} stroke={1.5} />
            </IconButton>
          </span>
        </Tooltip>
      </SecondaryToolbar>

      <Box sx={{ px: '16px', pb: '24px' }}>
        {loadError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {loadError}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataTableCard>
            <DataTable>
              <thead>
                <tr>
                  <DataTableHeadCell>Key</DataTableHeadCell>
                  <DataTableHeadCell>SKU / Product</DataTableHeadCell>
                  <DataTableHeadCell>Status</DataTableHeadCell>
                  <DataTableHeadCell>Customer</DataTableHeadCell>
                  <DataTableHeadCell>Ticket</DataTableHeadCell>
                  <DataTableHeadCell>Purchased</DataTableHeadCell>
                  <DataTableHeadCell align="right">Cost</DataTableHeadCell>
                  <DataTableHeadCell align="right">Actions</DataTableHeadCell>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 ? (
                  <DataTableEmpty colSpan={8}>No license keys found</DataTableEmpty>
                ) : (
                  visible.map((k) => (
                    <DataTableRow key={k.id}>
                      <DataTableCell>
                        {k.status === 'allocated' ? (
                          <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{k.key}</span>
                        ) : (
                          <span style={{ color: '#aaa', fontStyle: 'italic', fontSize: 12 }}>
                            Hidden until provisioned
                          </span>
                        )}
                      </DataTableCell>
                      <DataTableCell muted>{k.productName || k.sku || '—'}</DataTableCell>
                      <DataTableCell>
                        <Chip tone={STATUS_TONE[k.status]}>{k.status}</Chip>
                        {k.status === 'void' && k.voidReason && (
                          <span style={{ color: '#888', fontSize: 11, marginLeft: 6 }}>
                            {k.voidReason}
                          </span>
                        )}
                      </DataTableCell>
                      <DataTableCell muted>{k.currentCustomerName || '—'}</DataTableCell>
                      <DataTableCell muted>
                        {k.currentJobNumber ? `#${k.currentJobNumber}` : '—'}
                      </DataTableCell>
                      <DataTableCell muted>{formatDate(k.purchasedDate)}</DataTableCell>
                      <DataTableCell align="right">{formatCost(k.unitCost)}</DataTableCell>
                      <DataTableCell align="right">
                        {k.status === 'allocated' && (
                          <Tooltip title="Revoke (return to pool)">
                            <span>
                              <IconButton
                                size="small"
                                disabled={busyId === k.id}
                                onClick={() => setAction({ key: k, kind: 'revoke' })}
                              >
                                <IconArrowBackUp size={18} stroke={1.5} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {k.status === 'available' && (
                          <Tooltip title="Void (mark dead/unusable)">
                            <span>
                              <IconButton
                                size="small"
                                disabled={busyId === k.id}
                                onClick={() => setAction({ key: k, kind: 'void' })}
                              >
                                <IconBan size={18} stroke={1.5} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {k.status === 'void' && (
                          <Tooltip title="Un-void (return to pool)">
                            <span>
                              <IconButton
                                size="small"
                                disabled={busyId === k.id}
                                onClick={() => handleUnvoid(k)}
                              >
                                <IconArrowBackUp size={18} stroke={1.5} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </DataTableCell>
                    </DataTableRow>
                  ))
                )}
              </tbody>
            </DataTable>
          </DataTableCard>
        )}
      </Box>

      <Dialog open={action !== null} onClose={closeAction} maxWidth="sm" fullWidth>
        <DialogTitle>
          {action?.kind === 'revoke' ? 'Revoke license key' : 'Void license key'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box sx={{ fontFamily: 'monospace', fontSize: 12 }}>
              {action?.key.status === 'allocated' ? action.key.key : 'Hidden until provisioned'}
            </Box>
            <TextField
              label="Reason (optional)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              fullWidth
              autoFocus
              placeholder={
                action?.kind === 'revoke' ? 'e.g. mis-assigned' : 'e.g. already redeemed'
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAction} disabled={busyId !== null}>
            Cancel
          </Button>
          <Button variant="contained" onClick={runAction} disabled={busyId !== null}>
            {action?.kind === 'revoke' ? 'Revoke' : 'Void'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar !== null}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar?.severity ?? 'success'} onClose={() => setSnackbar(null)}>
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
